import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const LinkTreeApp = () => {
  const navigate = useNavigate();
  const [isElectron, setIsElectron] = useState(false);
  const [links, setLinks] = useState([]);
  const [profileData, setProfileData] = useState({
    name: 'Nume Utilizator',
    description: 'Descriere profil',
    avatar: '',
    copyright: 'LinkTree'
  });
  const [themeData, setThemeData] = useState({
    backgroundColor: 'bg-gradient-to-br from-purple-600 to-blue-500',
    cardBackground: 'bg-white',
    textColor: 'text-gray-800',
    linkHoverColor: 'bg-gray-100'
  });
  const [isLoaded, setIsLoaded] = useState(false);
  // Stare pentru a urmări meniul curent afișat
  const [currentMenu, setCurrentMenu] = useState('main');
  // Stare pentru a stoca directorul curent navigat
  const [currentDirectory, setCurrentDirectory] = useState(null);

  useEffect(() => {
    // Verifică dacă rulează în Electron - butoanele admin vor fi vizibile doar în Electron
    const checkElectron = async () => {
      if (window.electronAPI) {
        try {
          const electronCheck = await window.electronAPI.isElectron();
          setIsElectron(electronCheck);
        } catch (error) {
          setIsElectron(false);
        }
      } else {
        setIsElectron(false);
      }
    };
    
    checkElectron();
    
    // Încarcă fișierul JSON
    const loadData = async () => {
      try {
        const response = await fetch('/links.json');
        if (!response.ok) {
          throw new Error('Nu s-a putut încărca fișierul JSON');
        }
        const data = await response.json();
        
        setProfileData(data.profile);
        setLinks(data.links);
        if (data.theme) {
          setThemeData(data.theme);
        }
        setIsLoaded(true);
      } catch (error) {
        console.error('Eroare la încărcarea datelor:', error);
        // Folosim date demonstrative în caz de eroare
        const fallbackData = {
          theme: {
            backgroundColor: 'bg-gradient-to-br from-purple-600 to-blue-500',
            cardBackground: 'bg-white',
            textColor: 'text-gray-800',
            linkHoverColor: 'bg-gray-100'
          },
          profile: {
            name: 'Krakiun',
            description: 'Urmărește-mă pe rețelele sociale',
            avatar: '/api/placeholder/150/150',
            copyright: 'Krakiun'
          },
          links: [
            { id: 1, platform: 'facebook', title: 'Facebook', url: 'https://facebook.com/krakiun', icon: 'facebook' },
            { id: 2, platform: 'instagram', title: 'Instagram', url: 'https://instagram.com/krakiun', icon: 'instagram' },
            { id: 3, platform: 'tiktok', title: 'TikTok', url: 'https://tiktok.com/@familiablonda', icon: 'tiktok' }
          ]
        };
        
        setProfileData(fallbackData.profile);
        setLinks(fallbackData.links);
        setThemeData(fallbackData.theme);
        setIsLoaded(true);
      }
    };
    
    loadData();
  }, []);

  // Funcție pentru a naviga către un director
  const navigateToDirectory = (directory) => {
    setCurrentDirectory(directory);
    setCurrentMenu('directory');
  };

  // Funcție pentru a naviga înapoi la meniul principal
  const navigateToMainMenu = () => {
    setCurrentMenu('main');
    setCurrentDirectory(null);
  };

  // Funcție pentru a genera iconul potrivit platformei
  const getPlatformIcon = (platform, customIcon) => {
    // Dacă există un icon personalizat
    if (customIcon) {
      // Verifică dacă este un class de Font Awesome (fie formatul complet sau parțial)
      if (customIcon.startsWith('fa-') || customIcon.includes('fa-')) {
        // Asigurăm-ne că iconul are un prefix de familie (fa-solid, fa-brands etc.)
        let iconClass = customIcon;
        // Dacă nu are prefix, adăugăm fa-solid implicit
        if (!iconClass.includes('fa-solid') && !iconClass.includes('fa-brands') && 
            !iconClass.includes('fa-regular') && !iconClass.includes('fa-light') && 
            !iconClass.includes('fa-duotone')) {
          // Facem o presupunere inteligentă pentru rețele sociale
          if (iconClass.includes('fa-facebook') || iconClass.includes('fa-instagram') || 
              iconClass.includes('fa-twitter') || iconClass.includes('fa-tiktok') || 
              iconClass.includes('fa-youtube') || iconClass.includes('fa-whatsapp') || 
              iconClass.includes('fa-telegram') || iconClass.includes('fa-linkedin') || 
              iconClass.includes('fa-pinterest') || iconClass.includes('fa-discord') || 
              iconClass.includes('fa-snapchat') || iconClass.includes('fa-reddit') || 
              iconClass.includes('fa-github')) {
            iconClass = `fa-brands ${iconClass}`;
          } else {
            iconClass = `fa-solid ${iconClass}`;
          }
        }
        
        return (
          <i className={iconClass}></i>
        );
      }
      
      // Verifică dacă este un URL către o imagine
      if (customIcon.startsWith('http') || customIcon.startsWith('/')) {
        return (
          <img src={customIcon} alt="Icon" className="w-5 h-5" />
        );
      }
    }
    
    // Dacă nu există icon personalizat sau nu este valid, folosește iconul implicit
    switch (platform) {
      case 'facebook':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
          </svg>
        );
      case 'instagram':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
          </svg>
        );
      case 'tiktok':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <path d="M9 12a4 4 0 1 0 0 8 4 4 0 0 0 0-8z"></path>
            <path d="M15 8a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"></path>
            <path d="M15 8v8a4 4 0 0 1-4 4"></path>
            <line x1="15" y1="4" x2="21" y2="4"></line>
            <line x1="21" y1="4" x2="21" y2="8"></line>
          </svg>
        );
      case 'youtube':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
            <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
          </svg>
        );
      case 'whatsapp':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
          </svg>
        );
      case 'folder':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
          </svg>
        );
      case 'back':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        );
      case 'website':
      case 'globe':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="2" y1="12" x2="22" y2="12"></line>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="16"></line>
            <line x1="8" y1="12" x2="16" y2="12"></line>
          </svg>
        );
    }
  };

  // Funcție pentru deschiderea link-urilor
  const handleLinkClick = (url, e) => {
    e.preventDefault();
    
    if (isElectron && window.electronAPI) {
      // În Electron, link-ul va fi interceptat automat și deschis extern
      window.location.href = url;
    } else {
      // În browser, deschide în tab nou
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Renderează butonul de întoarcere la meniul principal
  const renderBackButton = () => (
    <button
      onClick={navigateToMainMenu}
      className={`w-full p-4 bg-gray-50 hover:${themeData.linkHoverColor} rounded-lg transition-all duration-200 mb-3 border border-gray-200`}
    >
      <div className="flex items-center">
        <div className="flex-shrink-0 mr-3">
          {getPlatformIcon('back')}
        </div>
        <div className="flex-1 text-left">
          <h2 className={`font-medium ${themeData.textColor}`}>Înapoi la meniul principal</h2>
        </div>
      </div>
    </button>
  );

  // Renderează meniul principal cu toate link-urile și folderele
  const renderMainMenu = () => (
    <div className="space-y-3">
      {links.map((link) => (
        <div key={link.id}>
          {link.type === 'folder' ? (
            <button
              onClick={() => navigateToDirectory(link)}
              className={`w-full p-4 bg-gray-50 hover:${themeData.linkHoverColor} rounded-lg transition-all duration-200 transform hover:-translate-y-1 border border-gray-200`}
            >
              <div className="flex items-center">
                <div className="flex-shrink-0 mr-3">
                  {getPlatformIcon(link.platform || 'folder', link.icon)}
                </div>
                <div className="flex-1 text-left">
                  <h2 className={`font-medium ${themeData.textColor}`}>{link.title}</h2>
                </div>
                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-gray-400">
                    <path d="M9 18l6-6-6-6"></path>
                  </svg>
                </div>
              </div>
            </button>
          ) : (
            <a
              href={link.url}
              onClick={(e) => handleLinkClick(link.url, e)}
              className={`block p-4 bg-gray-50 hover:${themeData.linkHoverColor} rounded-lg transition-all duration-200 transform hover:-translate-y-1 border border-gray-200`}
            >
              <div className="flex items-center">
                <div className="flex-shrink-0 mr-3">
                  {getPlatformIcon(link.platform, link.icon)}
                </div>
                <div className="flex-1">
                  <h2 className={`font-medium ${themeData.textColor}`}>{link.title}</h2>
                </div>
                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-gray-400">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </div>
              </div>
            </a>
          )}
        </div>
      ))}
    </div>
  );

  // Renderează submeniul unui folder
  const renderDirectoryMenu = () => (
    <div className="space-y-3">
      {renderBackButton()}
      
      <div className="mb-4 text-center">
        <h2 className={`text-xl font-bold ${themeData.textColor}`}>{currentDirectory?.title}</h2>
      </div>
      
      {currentDirectory?.children?.map((link) => (
        <a
          key={link.id}
          href={link.url}
          onClick={(e) => handleLinkClick(link.url, e)}
          className={`block p-4 bg-gray-50 hover:${themeData.linkHoverColor} rounded-lg transition-all duration-200 transform hover:-translate-y-1 border border-gray-200`}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0 mr-3">
              {getPlatformIcon(link.platform, link.icon)}
            </div>
            <div className="flex-1">
              <h2 className={`font-medium ${themeData.textColor}`}>{link.title}</h2>
            </div>
            <div>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-gray-400">
                <path d="M5 12h14"></path>
                <path d="m12 5 7 7-7 7"></path>
              </svg>
            </div>
          </div>
        </a>
      ))}
    </div>
  );

  return (
    <div className={`min-h-screen ${themeData.backgroundColor} flex flex-col items-center justify-center p-4 relative`}>
      {/* Floating Admin Button - doar în Electron */}
      {isElectron && (
        <button
          onClick={() => navigate('/builder')}
          className="fixed top-6 right-6 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-200 z-10"
          title="Deschide Admin Panel"
        >
          <i className="fa-solid fa-cog text-lg"></i>
        </button>
      )}

      <div className={`max-w-md w-full ${themeData.cardBackground} rounded-lg shadow-xl overflow-hidden`}>
        <div className="p-6 bg-gray-50">
          <div className="flex flex-col items-center">
            <img 
              src={profileData.avatar || "/api/placeholder/150/150"} 
              alt="Avatar"
              className="w-24 h-24 rounded-full border-4 border-white shadow-md mb-4"
            />
            <h1 className={`text-2xl font-bold ${themeData.textColor}`}>{profileData.name}</h1>
            <p className="text-gray-600 text-center mt-2">{profileData.description}</p>
          </div>
        </div>
        
        <div className="p-6">
          {!isLoaded ? (
            <div className="flex justify-center p-4">
              <p>Se încarcă...</p>
            </div>
          ) : (
            <>
              {currentMenu === 'main' && renderMainMenu()}
              {currentMenu === 'directory' && renderDirectoryMenu()}
            </>
          )}
        </div>
        
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <p className="text-center text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} {profileData.copyright || 'LinkTree'}
              {/* Indicație pentru dezvoltare - poate fi șters în producție */}
              {process.env.NODE_ENV === 'development' && (
                <span className="ml-2 text-xs">
                  {isElectron ? '🖥️ Electron' : '🌐 Browser'}
                </span>
              )}
            </p>
            {/* Buton Admin - doar în Electron */}
            {isElectron && (
              <button 
                onClick={() => navigate('/builder')}
                className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 transition-colors flex items-center"
                title="Admin Panel"
              >
                <i className="fa-solid fa-cog mr-1"></i> Admin
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkTreeApp;